import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Progress } from '@/components/ui/progress';
import { Key } from 'lucide-react';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001/api';

const RecuperarSenha = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1: email, 2: código e-mail + nova senha
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  const [formData, setFormData] = useState({
    email: '',
    codigo_email: '',
    nova_senha: '',
    confirmar_senha: ''
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
  };

  const handleSendCodes = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const response = await axios.post(`${API_BASE_URL}/password-recovery/send-codes`, {
        email: formData.email,
      });

      setSuccess('Código enviado! Verifique seu e-mail (e a pasta de spam).');
      setStep(2);
      
      if (response.data.dev_codes) {
        console.log('DEV - Código:', response.data.dev_codes);
        alert(`DEV — código por e-mail: ${response.data.dev_codes.codigo_email}`);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao enviar códigos. Verifique os dados e tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyAndReset = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Validações
    if (formData.nova_senha !== formData.confirmar_senha) {
      setError('As senhas não coincidem');
      return;
    }

    if (formData.nova_senha.length < 6) {
      setError('A senha deve ter no mínimo 6 caracteres');
      return;
    }

    setLoading(true);

    try {
      await axios.post(`${API_BASE_URL}/password-recovery/verify-and-reset`, {
        email: formData.email,
        codigo_email: formData.codigo_email,
        nova_senha: formData.nova_senha
      });

      setSuccess('Senha redefinida com sucesso! Redirecionando para o login...');
      setTimeout(() => {
        navigate('/login');
      }, 2000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao verificar o código. Verifique e tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const progress = (step / 2) * 100;

  return (
    <div className="min-h-full flex items-center justify-center py-12 px-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="relative z-10 w-full max-w-md"
      >
        <Card className="shadow-2xl border-0 bg-white/90 dark:bg-gray-800/90 backdrop-blur-xl">
          <CardHeader className="space-y-1 text-center">
            <div className="mx-auto w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mb-4">
              <Key className="w-8 h-8 text-primary" aria-hidden />
            </div>
            <CardTitle className="text-3xl font-bold bg-gradient-to-r from-[#0E76FF] to-[#2563EB] bg-clip-text text-transparent">
              Recuperar Senha
            </CardTitle>
            <CardDescription className="text-gray-600 dark:text-gray-400">
              {step === 1
                ? 'Informe o e-mail da conta para receber o código'
                : 'Digite o código recebido por e-mail e sua nova senha'}
            </CardDescription>
            {step === 2 && (
              <>
                <Progress value={progress} className="mt-4" />
                <p className="text-sm text-gray-500 dark:text-gray-400 text-center mt-2">
                  Etapa 2 de 2
                </p>
              </>
            )}
          </CardHeader>
          
          <CardContent>
            {error && (
              <Alert variant="destructive" className="mb-4 bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800">
                <AlertDescription className="text-red-800 dark:text-red-200">
                  {error}
                </AlertDescription>
              </Alert>
            )}

            {success && (
              <Alert className="mb-4 bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800">
                <AlertDescription className="text-green-800 dark:text-green-200">
                  {success}
                </AlertDescription>
              </Alert>
            )}

            {/* Etapa 1: E-mail */}
            {step === 1 && (
              <form onSubmit={handleSendCodes} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-gray-700 dark:text-gray-300">
                    Email *
                  </Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    placeholder="seu@email.com"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600"
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full bg-gradient-to-r from-[#0E76FF] to-[#2563EB] hover:from-[#0D6AE6] hover:to-[#1d4ed8] text-white font-semibold py-6 shadow-lg hover:shadow-xl transition-all"
                  disabled={loading}
                >
                  {loading ? (
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Enviando código...
                    </div>
                  ) : (
                    'Enviar código por e-mail'
                  )}
                </Button>
              </form>
            )}

            {/* Etapa 2: Código e nova senha */}
            {step === 2 && (
              <form onSubmit={handleVerifyAndReset} className="space-y-4">
                <Alert className="bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800">
                  <AlertDescription className="text-blue-800 dark:text-blue-200">
                    Enviamos um código de 6 dígitos para {formData.email}
                  </AlertDescription>
                </Alert>

                <div className="space-y-2">
                  <Label htmlFor="codigo_email" className="text-gray-700 dark:text-gray-300">
                    Código do e-mail *
                  </Label>
                  <Input
                    id="codigo_email"
                    name="codigo_email"
                    type="text"
                    placeholder="000000"
                    maxLength={6}
                    value={formData.codigo_email}
                    onChange={handleChange}
                    required
                    className="bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-center text-2xl tracking-widest"
                  />
                </div>

                <div className="border-t border-gray-200 dark:border-gray-700 my-4 pt-4">
                  <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                    Nova Senha
                  </h4>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="nova_senha" className="text-gray-700 dark:text-gray-300">
                    Nova Senha *
                  </Label>
                  <Input
                    id="nova_senha"
                    name="nova_senha"
                    type="password"
                    placeholder="Mínimo 6 caracteres"
                    value={formData.nova_senha}
                    onChange={handleChange}
                    required
                    className="bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirmar_senha" className="text-gray-700 dark:text-gray-300">
                    Confirmar Nova Senha *
                  </Label>
                  <Input
                    id="confirmar_senha"
                    name="confirmar_senha"
                    type="password"
                    placeholder="Digite a senha novamente"
                    value={formData.confirmar_senha}
                    onChange={handleChange}
                    required
                    className="bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600"
                  />
                </div>

                <div className="flex gap-3">
                  <Button
                    type="button"
                    onClick={() => setStep(1)}
                    variant="outline"
                    className="w-1/3"
                  >
                    ← Voltar
                  </Button>
                  <Button
                    type="submit"
                    className="w-2/3 bg-gradient-to-r from-[#0E76FF] to-[#2563EB] hover:from-[#0D6AE6] hover:to-[#1d4ed8] text-white font-semibold shadow-lg hover:shadow-xl transition-all"
                    disabled={loading}
                  >
                    {loading ? (
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Verificando...
                      </div>
                    ) : (
                      'Redefinir Senha'
                    )}
                  </Button>
                </div>

                <Button
                  type="button"
                  variant="link"
                  className="w-full text-sm text-blue-600 dark:text-blue-400"
                  onClick={handleSendCodes}
                  disabled={loading}
                >
                  🔄 Reenviar código
                </Button>
              </form>
            )}

            <div className="mt-6 text-center space-y-2">
              <Link
                to="/login"
                className="text-sm text-gray-500 dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 transition-colors block"
              >
                ← Voltar para Login
              </Link>
            </div>
          </CardContent>
        </Card>

        {/* Info Footer */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="mt-6 text-center text-sm text-gray-600 dark:text-gray-400"
        >
          <p>🔒 Processo seguro de recuperação de senha</p>
          <p className="text-xs mt-2">O código expira em 10 minutos</p>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default RecuperarSenha;
